'use client';

import { useEffect } from 'react';
import { InfoTooltip } from '../../shared/InfoTooltip';
import Tooltip from '../../shared/Tooltip';
import {
	citationCategories,
	getSourcesForCategory,
	getFieldsForSource
} from '../../../config/citation-config-new';
import ListManager from './list-manager';
import Select from '../../shared/Select';
import DatePicker from '../../shared/DatePicker';
import { parseOrganizationName } from '../../../utils/citationGenerator';

export default function CitationFormFields({
	selectedCategory,
	selectedSource,
	citationData,
	validationErrors = {},
	isEditing = false,
	onCategoryChange,
	onSourceChange,
	onInputChange,
	onListAdd,
	onListRemove,
	onListUpdate
}) {
	const sources = selectedCategory ? getSourcesForCategory(selectedCategory) : [];
	const fields =
		selectedCategory && selectedSource ? getFieldsForSource(selectedCategory, selectedSource) : [];

	// Handle default values for fields with defaultValue property
	useEffect(() => {
		if (fields.length > 0) {
			fields.forEach((field) => {
				if (field.defaultValue && !citationData[field.name] && field.disabled) {
					onInputChange(field.name, field.defaultValue);
				}
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedSource, selectedCategory]); // Run when source/category changes
	// Helper to determine tooltip placement based on label length
	const getTooltipPlacement = (label) => {
		return (label || '').length > 10 ? 'top' : 'right';
	};

	const renderField = (field) => {
		// Skip page range fields - they're handled specially in renderFields()
		if (field.name === 'pageRangeStart' || field.name === 'pageRangeEnd') {
			return null;
		}

		// Check showIf condition
		if (field.showIf) {
			const dependentValue = citationData[field.showIf.field];
			if (dependentValue !== field.showIf.value) {
				return null;
			}
		}

		// Handle redundant publisher for Government & Institutional Publications
		// APA 7th ed: Omit publisher if it is the same as the author
		if (
			selectedCategory === 'Government & Institutional Publications' &&
			(field.name === 'publisher' || field.name === 'websitePublisher')
		) {
			const publisherValue = (citationData[field.name] || '').trim().toLowerCase();
			let isRedundantPublisher = false;

			if (publisherValue) {
				if (citationData.AuthorOrOrganization === 'Organization' && citationData.organization) {
					const { fullName } = parseOrganizationName(citationData.organization);
					const cleanOrgName = fullName.trim().toLowerCase();
					isRedundantPublisher = publisherValue === cleanOrgName || publisherValue === citationData.organization.trim().toLowerCase();
				}
			}

			if (isRedundantPublisher) {
				return null;
			}
		}

		const fieldValue = citationData[field.name];

		// Date input fields - use DatePicker component
		if (field.type === 'date') {
			const currentValue = fieldValue || field.defaultValue || '';
			const hasError = validationErrors[field.name];

			return (
				<div className="mb-4" key={field.name}>
					<div className="flex items-center gap-2 mb-2">
						<label className="text-sm font-bold text-dark-blue">
							{field.label}
							{field.required && <span className="text-red-500">*</span>}
						</label>
						<Tooltip placement={getTooltipPlacement(field.label)}
							content={field.tooltip || `Enter the ${field.label.toLowerCase()} for this citation`}
						>
							<InfoTooltip />
						</Tooltip>
					</div>
					<DatePicker
						value={currentValue}
						onChange={(value) => onInputChange(field.name, value)}
						disabled={field.disabled || false}
						hasError={hasError}
						placeholder="2024, January 15"
					/>
					{hasError && <p className="mt-1 text-xs text-red-600">{validationErrors[field.name]}</p>}
				</div>
			);
		}

		// Text and URL input fields
		if (field.type === 'text' || field.type === 'url') {
			// Set default value if provided and field is empty
			const currentValue = fieldValue || field.defaultValue || '';
			const hasError = validationErrors[field.name];

			// Check if this is a year field (year, originalWorkPublishedYear, etc.)
			const isYearField = field.name === 'year' || field.name.toLowerCase().includes('year');

			// Check if this is a numeric-only field (volume, issue, season, episodeNumber)
			// For version: only numeric for Preprint, alphanumeric for other sources
			const isNumericOnlyField =
				field.name === 'volume' ||
				field.name === 'issue' ||
				field.name === 'season' ||
				field.name === 'episodeNumber' ||
				(field.name === 'version' && selectedSource === 'Preprint');

			// Set maxLength based on field name
			let maxLength;
			if (field.name === 'organization') {
				maxLength = 50;
			} else if (isYearField) {
				maxLength = 10; // Increased to allow for "n.d." or loose typing before validation
			} else {
				maxLength = undefined;
			}

			// Set placeholder based on field type and name
			let placeholder = '';
			if (field.type === 'url') {
				placeholder = 'https://example.com';
			} else if (isYearField) {
				placeholder = '2024 or n.d.';
			} else if (field.name === 'volume') {
				placeholder = '10';
			} else if (field.name === 'issue') {
				placeholder = '5';
			} else if (field.name === 'season') {
				placeholder = '3';
			} else if (field.name === 'episodeNumber') {
				placeholder = '12';
			} else if (field.name === 'version') {
				// Different placeholder based on source type
				placeholder = selectedSource === 'Preprint' ? '2' : 'v2.0.5';
			} else if (field.name === 'organization') {
				placeholder = 'Enter organization name';
			} else {
				placeholder = `Enter ${field.label.toLowerCase()}`;
			}

			// For year and numeric fields, input handling
			const shouldFilterNonNumeric = isNumericOnlyField;
			// For year, we allow numbers and "n.d." (and variants like "nd", "N.D.")
			// So we don't strictly filter to digits only on change, but we will filter other chars
			const isYearInput = isYearField;
			const inputType = field.type === 'url' ? 'url' : 'text';

			const handleChange = (e) => {
				const val = e.target.value;
				if (shouldFilterNonNumeric) {
					// Only allow numbers for numeric fields
					const value = val.replace(/[^0-9]/g, '');
					onInputChange(field.name, value);
				} else if (isYearInput) {
					// Only allow small letters for n.d.
					// Allow [0-9], ., n, d, space - case insensitive at input but lowercased storage
					const value = val.toLowerCase().replace(/[^0-9nd.\s]/g, '');
					onInputChange(field.name, value);
				} else {
					onInputChange(field.name, val);
				}
			};

			return (
				<div className="mb-4" key={field.name}>
					<div className="flex items-center gap-2 mb-2">
						<label className="text-sm font-bold text-dark-blue">
							{field.label}
							{field.required && <span className="text-red-500">*</span>}
						</label>
						<Tooltip placement={getTooltipPlacement(field.label)}
							content={field.tooltip || `Enter the ${field.label.toLowerCase()} for this citation`}
						>
							<InfoTooltip />
						</Tooltip>
					</div>
					<input
						type={inputType}
						value={currentValue}
						onChange={handleChange}
						placeholder={placeholder}
						disabled={field.disabled || false}
						maxLength={maxLength}
						inputMode={shouldFilterNonNumeric ? 'numeric' : (isYearInput ? 'text' : undefined)}
						pattern={shouldFilterNonNumeric ? '[0-9]*' : undefined}
						className={`w-full rounded-[10px] border px-4 xl:py-3 py-2 text-sm focus:ring-1 text-dark-blue outline-none ${field.disabled
							? 'bg-gray-100 cursor-not-allowed border-gray'
							: hasError
								? 'border-red-500 focus:ring-red-500'
								: 'border-gray focus:ring-purple'
							}`}
					/>
					{hasError && <p className="mt-1 text-xs text-red-600">{validationErrors[field.name]}</p>}
				</div>
			);
		}

		// Textarea fields
		if (field.type === 'textarea') {
			const hasError = validationErrors[field.name];

			return (
				<div className="mb-4" key={field.name}>
					<div className="flex items-center gap-2 mb-2">
						<label className="text-sm font-bold text-dark-blue">
							{field.label}
							{field.required && <span className="text-red-500">*</span>}
						</label>
						<Tooltip placement={getTooltipPlacement(field.label)}
							content={field.tooltip || `Enter the ${field.label.toLowerCase()} for this citation`}
						>
							<InfoTooltip />
						</Tooltip>
					</div>
					<textarea
						value={fieldValue || ''}
						onChange={(e) => onInputChange(field.name, e.target.value)}
						placeholder={`Enter ${field.label.toLowerCase()}`}
						rows={3}
						className={`w-full rounded-[10px] border px-4 xl:py-3 py-2 text-sm focus:ring-1 text-dark-blue outline-none resize-none ${hasError ? 'border-red-500 focus:ring-red-500' : 'border-gray focus:ring-purple'
							}`}
					/>
					{hasError && <p className="mt-1 text-xs text-red-600">{validationErrors[field.name]}</p>}
				</div>
			);
		}

		// Select dropdown fields
		if (field.type === 'select') {
			const hasError = validationErrors[field.name];

			return (
				<div className="mb-4" key={field.name}>
					<div className="flex items-center gap-2 mb-2">
						<label className="text-sm font-bold text-dark-blue">
							{field.label}
							{field.required && <span className="text-red-500">*</span>}
						</label>
						<Tooltip placement={getTooltipPlacement(field.label)}
							content={field.tooltip || `Select the ${field.label.toLowerCase()} from the dropdown`}
						>
							<InfoTooltip />
						</Tooltip>
					</div>
					<Select
						value={fieldValue || ''}
						onChange={(e) => onInputChange(field.name, e.target.value)}
						placeholder={`Select ${field.label}`}
						options={
							field.options?.map((option) => ({
								value: option,
								label: option
							})) || []
						}
						className={`text-sm ${hasError ? 'border-red-500' : ''}`}
					/>
					{hasError && <p className="mt-1 text-xs text-red-600">{validationErrors[field.name]}</p>}
				</div>
			);
		}

		// Radio button fields
		if (field.type === 'radio') {
			const hasError = validationErrors[field.name];
			// Disable Author/Organization radio buttons in edit mode
			const isLocked = isEditing && field.name === 'AuthorOrOrganization';

			return (
				<div className="mb-4" key={field.name}>
					<div className="flex items-center gap-2 mb-2">
						<label className="text-sm font-bold text-dark-blue">
							{field.label}
							{field.required && <span className="text-red-500">*</span>}
						</label>
						<Tooltip placement={getTooltipPlacement(field.label)} content={field.tooltip || `Choose the ${field.label.toLowerCase()} option`}>
							<InfoTooltip />
						</Tooltip>
					</div>
					<div className="flex gap-4">
						{field.options?.map((option) => (
							<label key={option} className={`flex items-center gap-2 ${isLocked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
								<input
									type="radio"
									name={field.name}
									value={option}
									checked={fieldValue === option}
									onChange={(e) => onInputChange(field.name, e.target.value)}
									disabled={isLocked}
									className="w-4 h-4 text-purple focus:ring-purple"
								/>
								<span className="text-sm text-dark-blue">{option}</span>
							</label>
						))}
					</div>
					{hasError && <p className="mt-1 text-xs text-red-600">{validationErrors[field.name]}</p>}
				</div>
			);
		}

		// List fields (for authors, editors, etc.)
		if (field.type === 'list') {
			const hasError = validationErrors[field.name];

			// Set placeholder based on field name
			let placeholder = 'Enter name';
			if (field.name === 'author' || field.name === 'authors') {
				placeholder = 'e.g., Smith, J. D.';
			} else if (field.name === 'editor' || field.name === 'editors') {
				placeholder = 'e.g., Johnson, A. B.';
			} else if (field.name === 'translator' || field.name === 'translators') {
				placeholder = 'e.g., Garcia, M. L.';
			}

			// Filter numbers for name fields (authors, editors, translators, etc.)
			const shouldFilterNumbers = true; // All list fields in citations are names

			return (
				<ListManager
					key={field.name}
					items={fieldValue || ['']}
					onAdd={() => onListAdd(field.name)}
					onRemove={(index) => onListRemove(field.name, index)}
					onUpdate={(index, value) => onListUpdate(field.name, index, value)}
					label={field.label}
					addButtonText={`Add ${field.label.toLowerCase()}`}
					required={field.required}
					tooltip={field.tooltip}
					error={hasError}
					maxLength={50}
					placeholder={placeholder}
					filterNumbers={shouldFilterNumbers}
					placement={getTooltipPlacement(field.label)}
				/>
			);
		}

		// Select-list fields (multiple selects synced to a list field)
		if (field.type === 'select-list') {
			// Find the corresponding list field - look for contributors, contributorsOrganization, presentersLecturers, etc.
			let linkedFieldName = null;
			if (field.name === 'contributorRoles') {
				// Check which contributor field exists in the current source type
				if (
					citationData.hasOwnProperty('contributors') ||
					fields.find((f) => f.name === 'contributors')
				) {
					linkedFieldName = 'contributors';
				} else if (
					citationData.hasOwnProperty('contributorsOrganization') ||
					fields.find((f) => f.name === 'contributorsOrganization')
				) {
					linkedFieldName = 'contributorsOrganization';
				}
			} else if (field.name === 'presenterRoles') {
				linkedFieldName = 'presentersLecturers';
			}

			const linkedList = linkedFieldName ? citationData[linkedFieldName] || [''] : [''];
			const rolesArray = fieldValue || [];

			return (
				<div className="mb-4" key={field.name}>
					<div className="flex items-center gap-2 mb-2">
						<label className="text-sm font-bold text-dark-blue">
							{field.label}
							{field.required && <span className="text-red-500">*</span>}
						</label>
						<Tooltip placement={getTooltipPlacement(field.label)}
							content={field.tooltip || `Select the role for each ${field.label.toLowerCase()}`}
						>
							<InfoTooltip />
						</Tooltip>
					</div>
					{linkedList.map((item, index) => (
						<div key={index} className="mb-2">
							<Select
								value={rolesArray[index] || ''}
								onChange={(e) => {
									const newRoles = [...rolesArray];
									newRoles[index] = e.target.value;
									onInputChange(field.name, newRoles);
								}}
								placeholder={`Select contributor ${index + 1} role`}
								options={
									field.options?.map((option) => ({
										value: option,
										label: option
									})) || []
								}
								className="text-sm"
							/>
						</div>
					))}
				</div>
			);
		}

		return null;
	};

	// Helper function to render fields, grouping page range fields together
	const renderFields = () => {
		const renderedFields = [];

		// Find page range fields
		const startField = fields.find((f) => f.name === 'pageRangeStart');
		const endField = fields.find((f) => f.name === 'pageRangeEnd');
		const hasPageRange = startField && endField;

		// Find page numbers fields
		const pageNumStartField = fields.find((f) => f.name === 'pageNumbersStart');
		const pageNumEndField = fields.find((f) => f.name === 'pageNumbersEnd');
		const hasPageNumbers = pageNumStartField && pageNumEndField;

		for (let i = 0; i < fields.length; i++) {
			const field = fields[i];

			// If this is the first page numbers field we encounter, render both together
			if (hasPageNumbers && field.name === 'pageNumbersStart') {
				const startValue =
					citationData[pageNumStartField.name] || pageNumStartField.defaultValue || '';
				const endValue = citationData[pageNumEndField.name] || pageNumEndField.defaultValue || '';
				const startError = validationErrors.pageNumbersStart;
				const endError = validationErrors.pageNumbersEnd;
				const pageNumbersError = validationErrors.pageNumbers;

				renderedFields.push(
					<div className="mb-4" key="pageNumbers">
						<div className="flex gap-4">
							{/* Start Field */}
							<div className="flex-1">
								<div className="flex items-center gap-1 mb-2">
									<label className="text-sm font-bold text-dark-blue">
										{pageNumStartField.label}
										{pageNumStartField.required && <span className="text-red-500">*</span>}
									</label>
									<Tooltip placement='right' content={pageNumStartField.tooltip || 'Enter the starting page number'}>
										<InfoTooltip />
									</Tooltip>
								</div>
								<input
									type="text"
									value={startValue}
									onChange={(e) => {
										onInputChange(pageNumStartField.name, e.target.value);
									}}
									placeholder="A1"
									disabled={pageNumStartField.disabled || false}
									className={`w-full rounded-[10px] border px-4 xl:py-3 py-2 text-sm focus:ring-1 text-dark-blue outline-none ${pageNumStartField.disabled
										? 'bg-gray-100 cursor-not-allowed border-gray'
										: startError || pageNumbersError
											? 'border-red-500 focus:ring-red-500'
											: 'border-gray focus:ring-purple'
										}`}
								/>
								{/* Show start field specific error */}
								{startError && !pageNumbersError && (
									<p className="mt-1 text-xs text-red-600">{startError}</p>
								)}
							</div>

							{/* End Field */}
							<div className="flex-1">
								<div className="flex items-center gap-1 mb-2">
									<label className="text-sm font-bold text-dark-blue">
										{pageNumEndField.label}
										{pageNumEndField.required && <span className="text-red-500">*</span>}
									</label>
									<Tooltip placement="left" content={pageNumEndField.tooltip || 'Enter the ending page number'}>
										<InfoTooltip />
									</Tooltip>
								</div>
								<input
									type="text"
									value={endValue}
									onChange={(e) => {
										onInputChange(pageNumEndField.name, e.target.value);
									}}
									placeholder="A5"
									disabled={pageNumEndField.disabled || false}
									className={`w-full rounded-[10px] border px-4 xl:py-3 py-2 text-sm focus:ring-1 text-dark-blue outline-none ${pageNumEndField.disabled
										? 'bg-gray-100 cursor-not-allowed border-gray'
										: endError || pageNumbersError
											? 'border-red-500 focus:ring-red-500'
											: 'border-gray focus:ring-purple'
										}`}
								/>
								{/* Show end field specific error */}
								{endError && !pageNumbersError && (
									<p className="mt-1 text-xs text-red-600">{endError}</p>
								)}
							</div>
						</div>
						{/* Show combined page numbers error below both fields */}
						{pageNumbersError && <p className="mt-1 text-xs text-red-600">{pageNumbersError}</p>}
					</div>
				);
			} else if (hasPageRange && field.name === 'pageRangeStart') {
				const startValue = citationData[startField.name] || startField.defaultValue || '';
				const endValue = citationData[endField.name] || endField.defaultValue || '';
				const startError = validationErrors.pageRangeStart;
				const endError = validationErrors.pageRangeEnd;
				const pageRangeError = validationErrors.pageRange;

				renderedFields.push(
					<div className="mb-4" key="pageRange">
						<div className="flex gap-4">
							{/* Start Field */}
							<div className="flex-1">
								<div className="flex items-center gap-2 mb-2">
									<label className="text-sm font-bold text-dark-blue">
										{startField.label}
										{startField.required && <span className="text-red-500">*</span>}
									</label>
									<Tooltip placement={getTooltipPlacement(startField.label)} content={startField.tooltip || 'Enter the starting page number'}>
										<InfoTooltip />
									</Tooltip>
								</div>
								<input
									type="text"
									value={startValue}
									onChange={(e) => {
										// Only allow numbers for page range
										const value = e.target.value.replace(/[^0-9]/g, '');
										onInputChange(startField.name, value);
									}}
									placeholder="15"
									disabled={startField.disabled || false}
									inputMode="numeric"
									pattern="[0-9]*"
									className={`w-full rounded-[10px] border px-4 xl:py-3 py-2 text-sm focus:ring-1 text-dark-blue outline-none ${startField.disabled
										? 'bg-gray-100 cursor-not-allowed border-gray'
										: startError || pageRangeError
											? 'border-red-500 focus:ring-red-500'
											: 'border-gray focus:ring-purple'
										}`}
								/>
								{/* Show start field specific error */}
								{startError && !pageRangeError && (
									<p className="mt-1 text-xs text-red-600">{startError}</p>
								)}
							</div>

							{/* End Field */}
							<div className="flex-1">
								<div className="flex items-center gap-2 mb-2">
									<label className="text-sm font-bold text-dark-blue">
										{endField.label}
										{endField.required && <span className="text-red-500">*</span>}
									</label>
									<Tooltip placement={getTooltipPlacement(endField.label)} content={endField.tooltip || 'Enter the ending page number'}>
										<InfoTooltip />
									</Tooltip>
								</div>
								<input
									type="text"
									value={endValue}
									onChange={(e) => {
										// Only allow numbers for page range
										const value = e.target.value.replace(/[^0-9]/g, '');
										onInputChange(endField.name, value);
									}}
									placeholder="42"
									disabled={endField.disabled || false}
									inputMode="numeric"
									pattern="[0-9]*"
									className={`w-full rounded-[10px] border px-4 xl:py-3 py-2 text-sm focus:ring-1 text-dark-blue outline-none ${endField.disabled
										? 'bg-gray-100 cursor-not-allowed border-gray'
										: endError || pageRangeError
											? 'border-red-500 focus:ring-red-500'
											: 'border-gray focus:ring-purple'
										}`}
								/>
								{/* Show end field specific error */}
								{endError && !pageRangeError && (
									<p className="mt-1 text-xs text-red-600">{endError}</p>
								)}
							</div>
						</div>
						{/* Show combined page range error below both fields */}
						{pageRangeError && <p className="mt-1 text-xs text-red-600">{pageRangeError}</p>}
					</div>
				);
			} else if (field.name !== 'pageRangeEnd' && field.name !== 'pageNumbersEnd') {
				// Skip pageRangeEnd and pageNumbersEnd since they're already rendered with their start fields
				// Render all other fields normally
				const rendered = renderField(field);
				if (rendered) {
					renderedFields.push(rendered);
				}
			}
		}

		return renderedFields;
	};

	return (
		<>
			{/* Category Selection */}
			<div className="mb-4">
				<div className="flex items-center gap-2 mb-2">
					<label className="text-sm font-bold text-dark-blue">
						Category <span className="text-red-500">*</span>
					</label>
					<Tooltip
						content="Select the category for your citation. This will determine the available source types."
						placement="bottom"
					>
						<InfoTooltip />
					</Tooltip>
				</div>
				<Select
					value={selectedCategory}
					onChange={(e) => onCategoryChange(e.target.value)}
					placeholder="Select Category"
					options={[...citationCategories]
						.sort((a, b) => a.localeCompare(b))
						.map((category) => ({
							value: category,
							label: category
						}))}
					className="text-sm"
				/>
			</div>

			{/* Source Selection */}
			<div className="mb-4">
				<div className="flex items-center gap-2 mb-2">
					<label className="text-sm font-bold text-dark-blue">
						Source Type <span className="text-red-500">*</span>
					</label>
					<Tooltip content="Select the specific source type for your citation. This will determine the required fields.">
						<InfoTooltip />
					</Tooltip>
				</div>
				<Select
					value={selectedSource}
					onChange={(e) => onSourceChange(e.target.value)}
					placeholder="Select Source Type"
					options={[...sources]
						.sort((a, b) => a.localeCompare(b))
						.map((source) => ({
							value: source,
							label: source
						}))}
					disabled={!selectedCategory}
					className="text-sm"
				/>
			</div>

			{/* Source Description */}
			{/* {sourceDescription && (
        <div className="xl:mb-6 mb-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
          <p className="text-xs text-dark-blue/80 leading-relaxed">{sourceDescription}</p>
        </div>
      )} */}

			{/* Dynamic Fields */}
			{fields.length > 0 && <div className="space-y-4">{renderFields()}</div>}
		</>
	);
}
